import type {
  SectionTenAssessment,
  SectionTenAssessmentV2,
  SectionTenClientDraft,
  SectionTenClientDraftV2,
  SectionTenConfirmationLineage,
  SectionTenLineage,
  SectionTenSourceSnapshot,
  SectionTenWorkflow,
} from "./domain";

const volatileKeys=new Set([
  "id","blockId","candidateId","sourceCandidateId","sourceBlockIds","recommendationIds",
  "createdAt","updatedAt","updatedBy","editedAt","editedBy","generatedAt","confirmedAt","confirmedBy",
  "capturedAt","capturedBy","recordedAt","recordedBy","migrationVersion",
  "sourceAssessmentConfirmedAt","sourceAssessmentConfirmationId",
]);
const unorderedArrayKeys=new Set([
  "recommendations","sourceReferences","dependencyIds","sourceSectionIds","evidenceIds",
]);

export function normaliseSectionTenSubstantiveText(value:string){
  return value.normalize("NFKC").replace(/\r\n?/g,"\n").replace(/[ \t]+/g," ").replace(/\n{3,}/g,"\n\n").trim();
}

function canonicalValue(value:unknown,key=""):unknown{
  if(typeof value==="string")return normaliseSectionTenSubstantiveText(value);
  if(value===null||typeof value!=="object")return value;
  if(Array.isArray(value)){
    const items=value.map(item=>canonicalValue(item,key));
    return unorderedArrayKeys.has(key)?items.sort((a,b)=>JSON.stringify(a).localeCompare(JSON.stringify(b))):items;
  }
  return Object.fromEntries(
    Object.entries(value as Record<string,unknown>)
      .filter(([name])=>!volatileKeys.has(name))
      .sort(([a],[b])=>a.localeCompare(b))
      .map(([name,item])=>[name,canonicalValue(item,name)]),
  );
}

export function stableSectionTenString(value:unknown){
  return JSON.stringify(canonicalValue(value));
}

export function sectionTenFingerprint(value:unknown){
  const input=stableSectionTenString(value);
  let result=2166136261;
  for(let index=0;index<input.length;index+=1){
    result^=input.charCodeAt(index);
    result=Math.imul(result,16777619);
  }
  return (result>>>0).toString(16).padStart(8,"0");
}

export function sectionTenSourceFingerprint(snapshot:SectionTenSourceSnapshot|undefined){
  if(!snapshot)return sectionTenFingerprint({source:"not-recorded"});
  if(snapshot.canonicalFingerprint)return snapshot.canonicalFingerprint;
  return sectionTenFingerprint({
    priorityText:snapshot.priorityText,
    sourceBlockFingerprint:snapshot.sourceBlockFingerprint,
    sectionSixCandidateFingerprint:snapshot.sectionSixCandidateFingerprint,
    sectionSourceFingerprints:snapshot.sectionSourceFingerprints,
  });
}

export function sectionTenAssessmentFingerprint(assessment:SectionTenAssessment|SectionTenAssessmentV2){
  return sectionTenFingerprint(assessment);
}

export function makeSectionTenConfirmationLineage(
  assessment:SectionTenAssessment|SectionTenAssessmentV2,
  sourceSnapshot:SectionTenSourceSnapshot|undefined,
  confirmedAt:string,
  confirmedBy?:string,
):SectionTenConfirmationLineage{
  const assessmentFingerprint=sectionTenAssessmentFingerprint(assessment),sourceFingerprint=sectionTenSourceFingerprint(sourceSnapshot);
  return{
    id:`section10-confirmation-${sectionTenFingerprint({assessmentFingerprint,sourceFingerprint})}`,
    assessmentFingerprint,
    sourceFingerprint,
    confirmedAt,
    confirmedBy,
    assessmentVersion:"version" in assessment&&assessment.version===2?2:1,
  };
}

export function sectionTenClientDraftIdentity(
  draft:SectionTenClientDraft|SectionTenClientDraftV2,
  sourceConfirmationId?:string,
){
  return `section10-client-${sectionTenFingerprint({
    wording:draft,
    sourceConfirmationId:sourceConfirmationId??draft.sourceAssessmentConfirmationId??"unlinked",
  })}`;
}

const appendUnique=<T extends {id?:string;draftId?:string;recordedAt?:string}>(items:T[],item:T,key:(value:T)=>string)=>
  items.some(existing=>key(existing)===key(item))?items:[...items,item];

function emptyLineage():SectionTenLineage{
  return{version:1,confirmationHistory:[],approvalHistory:[],completionHistory:[],reconciliations:[]};
}

export function migrateSectionTenLineage(workflow:SectionTenWorkflow):SectionTenWorkflow{
  const assessment=workflow.redesignVersion===2?workflow.v2ConfirmedAssessment:workflow.confirmedAssessment;
  const confirmedAt=workflow.redesignVersion===2?workflow.v2ConfirmedAt:workflow.confirmedAt;
  const confirmedBy=workflow.redesignVersion===2?workflow.v2ConfirmedBy:workflow.confirmedBy;
  const sourceSnapshot=workflow.redesignVersion===2?workflow.v2SourceSnapshot:workflow.sourceSnapshot;
  const draft=workflow.redesignVersion===2?workflow.v2ClientDraft:workflow.clientDraft;
  const oldApproved=workflow.redesignVersion===2?workflow.v2ClientDraftApproved:workflow.clientDraftApproved;
  const oldApprovedAt=workflow.redesignVersion===2?workflow.v2ClientDraftApprovedAt:workflow.clientDraftApprovedAt;
  const lineage=workflow.sectionTenLineage??emptyLineage();
  const confirmation=assessment&&confirmedAt
    ? makeSectionTenConfirmationLineage(assessment,sourceSnapshot,confirmedAt,confirmedBy)
    : lineage.confirmation;
  let confirmationHistory=[...lineage.confirmationHistory];
  if(confirmation)confirmationHistory=appendUnique(confirmationHistory,confirmation,item=>item.id);
  const suppliedSourceId=draft?.sourceAssessmentConfirmationId;
  const timestampProvesLink=Boolean(draft&&confirmation&&draft.sourceAssessmentConfirmedAt===confirmedAt);
  const sourceConfirmationId=suppliedSourceId??(timestampProvesLink?confirmation?.id:lineage.currentDraftSourceConfirmationId);
  const sourceIsKnown=Boolean(sourceConfirmationId&&confirmationHistory.some(item=>item.id===sourceConfirmationId));
  const draftId=draft?sectionTenClientDraftIdentity(draft,sourceIsKnown?sourceConfirmationId:undefined):undefined;
  let approvalHistory=[...lineage.approvalHistory];
  const inferredApprovedAt=oldApprovedAt??workflow.clientUpdatedAt??draft?.generatedAt??confirmedAt;
  if(draftId&&confirmation&&sourceConfirmationId===confirmation.id&&oldApproved&&inferredApprovedAt){
    approvalHistory=appendUnique(approvalHistory,{draftId,confirmationId:confirmation.id,recordedAt:inferredApprovedAt,recordedBy:confirmedBy},item=>`${item.draftId}|${item.recordedAt}`);
  }
  let completionHistory=[...lineage.completionHistory];
  if(draftId&&confirmation&&sourceConfirmationId===confirmation.id&&workflow.v2CompletedAt){
    completionHistory=appendUnique(completionHistory,{draftId,confirmationId:confirmation.id,recordedAt:workflow.v2CompletedAt,recordedBy:workflow.v2CompletedBy},item=>`${item.draftId}|${item.recordedAt}`);
  }
  const approvedDraftId=lineage.approvedDraftId??approvalHistory.find(item=>item.draftId===draftId&&item.confirmationId===confirmation?.id)?.draftId;
  const completedDraftId=lineage.completedDraftId??completionHistory.find(item=>item.draftId===draftId&&item.confirmationId===confirmation?.id)?.draftId;
  const requiresReconciliation=Boolean(draft&&confirmation&&sourceConfirmationId!==confirmation.id);
  const next:SectionTenWorkflow={
    ...workflow,
    sectionTenLineageMigrationVersion:1,
    sectionTenLineage:{
      ...lineage,
      version:1,
      confirmation,
      confirmationHistory,
      currentDraftId:draftId,
      currentDraftSourceConfirmationId:sourceIsKnown?sourceConfirmationId:undefined,
      approvedDraftId,
      completedDraftId,
      approvalHistory,
      completionHistory,
      reconciliations:[...lineage.reconciliations],
    },
    v2ClientDraft:workflow.v2ClientDraft&&sourceIsKnown
      ?{...workflow.v2ClientDraft,sourceAssessmentConfirmationId:sourceConfirmationId}
      :workflow.v2ClientDraft,
    clientDraft:workflow.clientDraft&&sourceIsKnown
      ?{...workflow.clientDraft,sourceAssessmentConfirmationId:sourceConfirmationId}
      :workflow.clientDraft,
    v2ClientDraftIdentity:workflow.redesignVersion===2?draftId:workflow.v2ClientDraftIdentity,
    v2ClientDraftRequiresRecoveryReview:workflow.redesignVersion===2?requiresReconciliation:workflow.v2ClientDraftRequiresRecoveryReview,
  };
  return JSON.stringify(next)===JSON.stringify(workflow)?workflow:next;
}

export interface ResolvedSectionTenLineageState {
  confirmationId?:string;
  draftId?:string;
  confirmedSourceCurrent:boolean;
  draftRelationship:"Current"|"Stale"|"Unlinked"|"Unavailable";
  reconciliationRequired:boolean;
  approvalCurrent:boolean;
  completionCurrent:boolean;
}

export function resolveSectionTenLineageState(workflow:SectionTenWorkflow,currentSourceSnapshot?:SectionTenSourceSnapshot):ResolvedSectionTenLineageState{
  const migrated=migrateSectionTenLineage(workflow),lineage=migrated.sectionTenLineage!;
  const draft=migrated.redesignVersion===2?migrated.v2ClientDraft:migrated.clientDraft;
  const confirmation=lineage.confirmation;
  const confirmedSourceCurrent=!currentSourceSnapshot||!confirmation||confirmation.sourceFingerprint===sectionTenSourceFingerprint(currentSourceSnapshot);
  const relationship:ResolvedSectionTenLineageState["draftRelationship"]=!draft
    ?"Unavailable"
    :!lineage.currentDraftSourceConfirmationId
      ?"Unlinked"
      :lineage.currentDraftSourceConfirmationId===confirmation?.id
        ?"Current"
        :"Stale";
  const approvalCurrent=Boolean(
    relationship==="Current"&&lineage.currentDraftId&&lineage.approvedDraftId===lineage.currentDraftId&&
    lineage.approvalHistory.some(item=>item.draftId===lineage.currentDraftId&&item.confirmationId===confirmation?.id),
  );
  const completionCurrent=Boolean(
    approvalCurrent&&lineage.currentDraftId&&lineage.completedDraftId===lineage.currentDraftId&&
    lineage.completionHistory.some(item=>item.draftId===lineage.currentDraftId&&item.confirmationId===confirmation?.id),
  );
  return{
    confirmationId:confirmation?.id,
    draftId:lineage.currentDraftId,
    confirmedSourceCurrent,
    draftRelationship:relationship,
    reconciliationRequired:relationship==="Stale"||relationship==="Unlinked",
    approvalCurrent,
    completionCurrent,
  };
}

export function linkSavedSectionTenDraft(workflow:SectionTenWorkflow,draft:SectionTenClientDraftV2):SectionTenWorkflow{
  const migrated=migrateSectionTenLineage(workflow),confirmation=migrated.sectionTenLineage?.confirmation;
  const sourceConfirmationId=draft.sourceAssessmentConfirmationId??confirmation?.id;
  const linked={...draft,sourceAssessmentConfirmationId:sourceConfirmationId};
  const draftId=sectionTenClientDraftIdentity(linked,sourceConfirmationId);
  return{
    ...migrated,
    v2ClientDraft:linked,
    v2ClientDraftIdentity:draftId,
    v2ClientDraftRequiresRecoveryReview:Boolean(!confirmation||sourceConfirmationId!==confirmation.id),
    sectionTenLineage:{
      ...migrated.sectionTenLineage!,
      currentDraftId:draftId,
      currentDraftSourceConfirmationId:sourceConfirmationId,
    },
  };
}

export function recordSectionTenApproval(workflow:SectionTenWorkflow,recordedAt:string,recordedBy="Demo Operator"){
  const migrated=migrateSectionTenLineage(workflow),lineage=migrated.sectionTenLineage!,draftId=lineage.currentDraftId,confirmationId=lineage.confirmation?.id;
  if(!draftId||!confirmationId||lineage.currentDraftSourceConfirmationId!==confirmationId)return migrated;
  const record={draftId,confirmationId,recordedAt,recordedBy};
  return{...migrated,sectionTenLineage:{...lineage,approvedDraftId:draftId,approvalHistory:appendUnique(lineage.approvalHistory,record,item=>`${item.draftId}|${item.recordedAt}`)}};
}

export function recordSectionTenCompletion(workflow:SectionTenWorkflow,recordedAt:string,recordedBy="Demo Operator"){
  const migrated=migrateSectionTenLineage(workflow),state=resolveSectionTenLineageState(migrated),lineage=migrated.sectionTenLineage!,draftId=lineage.currentDraftId,confirmationId=lineage.confirmation?.id;
  if(!state.approvalCurrent||!draftId||!confirmationId)return migrated;
  const record={draftId,confirmationId,recordedAt,recordedBy};
  return{...migrated,sectionTenLineage:{...lineage,completedDraftId:draftId,completionHistory:appendUnique(lineage.completionHistory,record,item=>`${item.draftId}|${item.recordedAt}`)}};
}
